import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { DateBox } from "devextreme-react/date-box";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import DataGrid, {
  Column,
  Paging,
  Pager,
} from 'devextreme-react/data-grid';
import { LoadPanel } from 'devextreme-react/load-panel'; // Import LoadPanel for loading indicator
import { useLocation } from 'react-router-dom'; // Import useLocation to access navigation state

const schema = () =>
  yup.object().shape({
    bioEntryDate: yup.date().required("Required"),
  });


const ChangeLmisDate = () => {
  const location = useLocation(); // Access the location object
  const selectedIds = location.state?.selectedIds; // Retrieve selectedIds from navigation state

  const [isLoading, setIsLoading] = useState(false); // Track loading state
  const [applicants, setApplicants] = useState([]);
  const navigate = useNavigate();  

  const {
      register,
      handleSubmit,
      setValue,
      watch,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(schema()),
    });

    const handleDateChange = (fieldName, setValue) => (e) => {
        if (e.value) {
            setValue(fieldName, formatDateToYYYYMMDD(e.value));
        }
    };
    
    
    // Utility function to format as "YYYY-MM-DD" (avoiding UTC conversion)
    const formatDateToYYYYMMDD = (date) => {
        if (!date) return "";
        
        const d = new Date(date);
        d.setHours(0, 0, 0, 0); // Ensure local time without shifting
        
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
    
        return `${year}-${month}-${day}`;
    };
    
    

  useEffect(() => {
    // Early return if no selectedIds are provided
    if (!selectedIds || selectedIds.length === 0) {
      console.error("No selected IDs provided.");
      return;
    }

    setIsLoading(true); // Set loading to true before API call

      api.post('/Applicants/selected',
        selectedIds, // Pass selectedIds directly as the request body
      )
      .then((res) => {
        const transformedData = res.data.map((item) => ({
          ...item,
          ID: item.id,
        }));
        setApplicants(transformedData);
      })
      .catch((err) => {
        console.error('Error fetching applicants:', err);
      })
      .finally(() => setIsLoading(false)); // Set loading to false after API call
  }, [selectedIds]); // Include selectedIds in the dependency array

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    d.setHours(0, 0, 0, 0); // Ensure local time without shifting
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleFormSubmit = async (data) => {
    const { bioEntryDate } = data; // Extract form data

    if (!selectedIds || selectedIds.length === 0) {
        alert("Please select at least one applicant.");
        return;
    }

    if (!bioEntryDate) {
        alert("Please fill in all required fields.");
        return;
    }

    setIsLoading(true);

    try {
        const bioEntryDate = watch("bioEntryDate"); // Should be already formatted as YYYY-MM-DD
        const formattedDate = formatDate(bioEntryDate);

        const response = await api.post(`/Applicants/changeLmisDate?bioEntryDate=${formattedDate}`,
            selectedIds, // Send selectedIds as the request body
            {
                headers: {
                    "Content-Type": "application/json",
                },
            }
        );

        navigate("/applicants/applicantlist");
    } catch (error) {
        console.error("Error Response:", error.response?.data);
        alert(JSON.stringify(error.response?.data, null, 2));
    } finally {
        setIsLoading(false);
    }
};


  // Use useMemo to optimize column definitions
  const columns = useMemo(
    () => [
      { field: 'passportNo', caption: 'Passport No' },
      { field: 'fullName', caption: 'Full Name' },
      { field: 'bioEntryDate', caption: 'Lmis Date' },
    ],
    []
  );

  return (

    <div className="container mt-4" style={{ maxWidth: "1100px" , maxHeight: "600px", marginBottom: "20px"}}>
      <h5 className="es-label-title">CHANGE STATUS</h5>
      <div className="card p-3">
      <form onSubmit={handleSubmit((data) => handleFormSubmit(data))} className="space-y-4">

        <div className="w-100 h-100 bg-white rounded p-1">
      {/* LoadPanel for loading indicator */}
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: '.dx-datagrid' }}
        visible={isLoading}
        showIndicator={true}
        shading={false}
        showPane={true}
      />      
    </div>
    
<div className="row mb-1">
        <div className="col-md-6">
            <div className="row">
                <label className="col-sm-4 col-form-label es-field-label"> LMIS Date
                </label>
                
                <div className="col-sm-8 d-flex align-items-center">
                    <DateBox
                        value={watch("bioEntryDate")}
                        displayFormat="dd/MM/yyyy" // Ensures proper date format
                        placeholder="DD/MM/YYYY"  // Watermark
                        onValueChanged={handleDateChange("bioEntryDate", setValue)}
                        min={new Date("2000-01-01")}
                    />
                    {errors.bioEntryDate && (<i className="bi bi-exclamation-circle text-danger ms-1"></i>)}
                </div>
              
              <div className="d-flex justify-content-center">
                <hr></hr>
                   <button type="submit" className="btn es-button"> SAVE </button>
              </div>
            </div>
        </div>
        <div className="col-md-6">
          <div className="row">
                <DataGrid
                    dataSource={applicants}
                    showBorders
                    rowAlternationEnabled
                    allowColumnResizing
                    columnAutoWidth
                    focusedRowEnabled
                    keyExpr="ID"
                    height="300px"
                    width="500px"
                >
                    <Paging defaultPageSize={15} />
                    <Pager showPageSizeSelector allowedPageSizes={[10, 20, 50]} showInfo />

                    {columns.map(({ field, caption }) => (
                    <Column key={field} dataField={field} caption={caption} allowFiltering
                        format={field === 'bioEntryDate' ? 'dd/MM/yyyy' : undefined}  
                        dataType={field === 'bioEntryDate' ? 'date' : undefined} 
                    />
                    ))}
                </DataGrid>
          </div>
        </div>
      </div>          
        </form>
        </div>
        </div>
  );
};

export default ChangeLmisDate;